import {
  Box,
  Grid,
  MenuItem,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";
import CustomSelect from "../../../../../components/common/Select";
import Button from "../../../../../components/common/Button";
import { Delete } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import apiService from "../../../../../service/Api";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import domtoimage from "dom-to-image";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { get } from "http";
import { set } from "date-fns";
import { showErrorToast } from "../../../../../components/common/ShowToast";

interface Image {
  id: number;
  content_type: string;
  created_at: Date;
  deceased: number;
  description: string;
  file: string;
  title: string;
  updated_at: Date;
  status: string;
}

export const MemoryTableGallery: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [fetchedImages, setFetchedImages] = useState<Image[]>([]);
  const [renderingImages, setRenderingImages] = useState<Image[]>([]);
  const [checkedImagesIDs, setCheckedImagesIDs] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const bulkSelectAction = useRef<any>("approve");

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setIsLoading(true);
    setError(false);
    try {
      const response = await apiService.get<any>(
        `/api/individuals/deceased-gallery/?stream_id=${id}`
      );

      console.log("Fetched images:", response.data.results);

      const images: Image[] = response.data.results.filter(
        (image: Image) => image.content_type === "image"
      );

      const activeImages = images.filter((image) => image.status !== "spam");

      setRenderingImages([...activeImages]);
      setFetchedImages([...images]);
    } catch (error) {
      toast.error("Failed to load images. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(false);
    } finally {
      setIsLoading(false);
    }
  };

  const filterImages = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & {
          target: {
            value: unknown;
            name: string;
          };
        })
  ) => {
    const value = e.target.value;

    switch (value) {
      case "all":
        setRenderingImages(fetchedImages);
        break;
      case "approved":
        //setRenderingImages(fetchedComments.filter((c) => c.source));
        break;
      case "unapproved":
        //setRenderingImages(fetchedImages.filter((i) => !i.source));
        break;
      default:
      //setRenderingImages(fetchedImages);
    }
  };

  const addImageToChecked = (id: number) => {
    setCheckedImagesIDs([...checkedImagesIDs, id]);
  };

  const removeImageToChecked = (id: number) => {
    const newCheckedImages = checkedImagesIDs.filter(
      (imageID) => imageID !== id
    );
    setCheckedImagesIDs([...newCheckedImages]);
  };

  const toggleChecked = () => {
    if (checkIsChecked()) {
      setCheckedImagesIDs([]);
      return;
    } else {
      const imagesID: number[] = renderingImages.map((image) => image.id);
      setCheckedImagesIDs([...imagesID]);
    }
  };

  const checkIsChecked = () => {
    const renderingLength = renderingImages.length;
    const checkedCommentsLength = checkedImagesIDs.length;

    if (renderingLength === 0 && checkedCommentsLength === 0) {
      return false;
    }

    if (renderingLength === checkedCommentsLength) {
      return true;
    }

    return false;
  };

  const updateBulkAction = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & {
          target: {
            value: unknown;
            name: string;
          };
        })
  ) => {
    bulkSelectAction.current = e.target.value;
  };

  const bulkAction = async () => {
    const action = bulkSelectAction.current;

    if (checkedImagesIDs.length === 0) {
      toast.warning(`Please select comments for bulk action`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }

    try {
      await apiService.post(
        `/api/individuals/deceased-gallery/bulk-${action}/`,
        {
          files_ids: checkedImagesIDs,
        }
      );

      toast.success(`Images ${action}`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      fetchImages();
    } catch (error) {
      toast.error("Failed to update status.Please try again", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const downloadApproved = async () => {
    setIsDownloading(true);

    try {
      const response = await apiService.get<any>(
        `/api/individuals/deceased-gallery/${fetchedImages[0].deceased}/bulk-download/`
      );

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "approved_images.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      showErrorToast("Failed to download approved images");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box bgcolor={reduxTheme.primary_colors[900]} p={2} borderRadius={1}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Grid container gap={3} alignItems={"center"} mt={2}>
          <Typography variant="h5" fontWeight={"bold"}>
            Images and Media
          </Typography>

          <Box>
            <CustomSelect
              //onChange={(e) => filterComments(e)}
              label={""}
              defaultValue={"all"}
              sx={{ width: "130px", borderRadius: "30px" }}
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </CustomSelect>
          </Box>

          <Box display={"flex"} gap={2} alignContent={"center"}>
            <CustomSelect
              onChange={(e) => updateBulkAction(e)}
              label={"Bulk Actions"}
              defaultValue={"approve"}
              sx={{ width: "150px" }}
              size="small"
            >
              <MenuItem value="approve">Approve</MenuItem>
              <MenuItem value="spam">Spam</MenuItem>
              <MenuItem value="unapprove">Unapprove</MenuItem>
            </CustomSelect>
            <Button variant="contained" onClick={bulkAction}>
              Apply
            </Button>
          </Box>
        </Grid>

        <Button
          variant="contained"
          onClick={downloadApproved}
          disabled={isDownloading}
        >
          {isDownloading ? <CircularProgress size={24} /> : "Download Approved"}
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: 3 }}>
        <Checkbox checked={checkIsChecked()} onClick={toggleChecked} />
        <Typography variant="body1">Select All</Typography>
      </Box>

      {isLoading && (
        <Box display={"flex"} justifyContent={"center"} pt={4} pb={4}>
          <CircularProgress size={30} />
        </Box>
      )}

      {error && !isLoading && (
        <Box justifyContent={"center"} pt={4} pb={4}>
          <Typography variant="body1" textAlign={"center"} mt={1}>
            Failed to fetch images
          </Typography>
          <Box display={"flex"} justifyContent={"center"}>
            <Button variant="contained" sx={{ mt: 2 }}>
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {!error && !isLoading && renderingImages.length === 0 && (
        <Box justifyContent={"center"} pt={4} pb={4}>
          <Typography variant="body1" textAlign={"center"} mt={1}>
            No images available at the moment
          </Typography>
        </Box>
      )}

      {
        <Grid container gap={2} mt={2} p={1}>
          {renderingImages.map((image) => {
            return (
              <GalleryImage
                image={image}
                images={renderingImages}
                fetchImages={fetchImages}
                checkedImagesIDs={checkedImagesIDs}
                removeImageToChecked={removeImageToChecked}
                addImageToChecked={addImageToChecked}
                downloadApproved={downloadApproved}
                bulkAction={bulkAction}
              />
            );
          })}
        </Grid>
      }
    </Box>
  );
};

interface GalleryImageProps {
  image: Image;
  images: Image[];
  checkedImagesIDs: number[];
  removeImageToChecked: (id: number) => void;
  addImageToChecked: (id: number) => void;
  fetchImages: () => {};
  downloadApproved: () => void;
  bulkAction: () => void;
}

const GalleryImage: React.FC<GalleryImageProps> = ({
  image,
  fetchImages,
  images,
  checkedImagesIDs,
  removeImageToChecked,
  addImageToChecked,
}) => {
  console.log("Rendering image:", image);
  const imageID = image.id;
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const date = new Date(image.created_at);

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  };

  const formattedDate = new Intl.DateTimeFormat("en-GB", options).format(date);

  useEffect(() => {
    checkIfImageIsChecked();
  }, [checkedImagesIDs]);

  const getStatusChipProps = (status: string) => {
    switch (status) {
      case "unapproved":
        return {
          label: "In Progress",
          bgcolor: "#FEF3C7",
          color: "#D97706",
        };
      case "approved":
        return {
          label: "Confirmed",
          bgcolor: "#F0FDFA",
          color: "#0D9488",
        };

      default:
        return {
          label: "Unknown",
          bgcolor: "#BDBDBD40",
          color: "#BDBDBD",
        };
    }
  };

  const checkIfImageIsChecked = () => {
    const isAvailable = checkedImagesIDs.some((id) => id === image.id);
    setIsChecked(isAvailable);
  };

  const singleImageAction = async (action: string) => {
    setIsDeleting(true);
    setError(false);
    try {
      await apiService.post(
        `/api/individuals/deceased-gallery/bulk-${action}/`,
        {
          files_ids: [imageID],
        }
      );

      fetchImages();
      toast.success(`Image ${action}ed.`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error) {
      toast.error(`Failed to ${action} image. Please try again.`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCheckToggle = () => {
    if (isChecked) {
      removeImageToChecked(image.id);
    } else {
      addImageToChecked(image.id);
    }
  };

  return (
    <Paper key={image.id} sx={{ width: 260, height: 300 }}>
      {/* Image container */}

      <Box
        sx={{
          width: "100%",
          height: "60%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Checkbox
          checked={isChecked}
          onClick={(e) => handleCheckToggle()}
          sx={{
            width: 30,
            height: 30,
            backgroundColor: reduxTheme.primary_colors[900],
            borderRadius: "50%",
            position: "absolute",
            left: "5px",
            top: "5px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        />

        <Chip
          label={image.status === "unapproved" ? "Pending" : "Approved"}
          sx={{
            position: "absolute",
            top: 5,
            right: 5,
            backgroundColor: getStatusChipProps(image.status).bgcolor,
            color: getStatusChipProps(image.status).color,
          }}
        />
        <img
          src={image.file}
          alt={"Upload image"}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      <Stack spacing={1} sx={{ p: 1, mb: 1 }}>
        <Typography variant="body2">
          <PersonOutlineIcon
            fontSize="small"
            sx={{ verticalAlign: "middle", mr: 0.5 }}
          />
          {image.title}
        </Typography>
        <Typography variant="body2">
          <CalendarTodayIcon
            fontSize="small"
            sx={{ verticalAlign: "middle", mr: 0.5 }}
          />
          {formattedDate}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 1,
          pl: 1,
          pr: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={() =>
            singleImageAction(
              image.status === "unapproved" ? "approve" : "unapprove"
            )
          }
        >
          {image.status === "unapproved" ? "Approve" : "Unapprove"}
        </Button>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#E11D48" }}
          onClick={() => singleImageAction("spam")}
        >
          Spam
        </Button>
      </Box>
    </Paper>
  );
};
